<script setup lang="ts">
import { onMounted, ref, type Ref } from "vue";
import { ProfileAdministrationAPI } from "../../api";
import SupportSearchBar from "../SupportSearchBar.vue";
import EmailDeliveryStatus from "../EmailDeliveryStatus.vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useConfirm } from "primevue/useconfirm";
import { useToast } from "primevue/usetoast";
import { Distributor, EmailReferenceType, Product, PurchasedProduct, Trainer, Transaction } from "../../types/PersologOnlineTypes.ts";
import { EditableProduct, fetchAllProductsForTrainer } from "../../utils/ProductUtils.ts";
import { formatDateTime } from "../../utils/dateFormat.ts";
import { watch } from "vue";

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const confirm = useConfirm();
const toast = useToast();

const showError = (error: unknown, detail: string) => {
    console.error(detail, error);
    toast.add({ severity: "error", summary: t("messages.error"), detail, life: 4000 });
};

const email = ref("");
const expandedRows = ref([]);
const trainers: Ref<Trainer[]> = ref([]);
const distributors: Ref<Distributor[]> = ref([]);
const selectedForDropdown = ref(null) as Ref<Trainer | null>;
const editableProducts = ref<Record<string, EditableProduct[]>>({});
const transactions = ref<Record<string, Transaction[]>>({});
const trainerDataLoading = ref<Record<string, boolean>>({});
const editQuantityDialogVisible = ref(false);
const quantity = ref(0);
const searched = ref(false);


const product = ref<PurchasedProduct>();

let selectedDistributor: Distributor;

const searchTrainers = async () => {
    const normalizedEmail = email.value.trim();
    const nextQuery = { ...route.query };

    if (normalizedEmail) {
        nextQuery.email = normalizedEmail;
        email.value = normalizedEmail;
    } else {
        delete nextQuery.email;
    }

    await router.replace({ query: nextQuery });
    await fetchTrainers();
};

/**
 * Fetches trainers based on the current email query parameter and updates the trainers state.
 * Does not fetch trainers data.
 * The backend returns the matches in no defined order, so they are sorted by email here.
 */
const fetchTrainers = async () => {
    try {
        trainers.value = (await ProfileAdministrationAPI.fetchTrainers(email.value))
            .sort((a, b) => a.email.localeCompare(b.email, undefined, { sensitivity: "base" }));
    } catch (error) {
        // A failed search used to be indistinguishable from "no trainer with that address".
        trainers.value = [];
        showError(error, t("messages.load.error"));
    } finally {
        searched.value = true;
    }
};

/**
 * Fetches products and transactions for a specific trainer. Used when expanding a trainer row to show details.
 * @param trainerId
 */
const loadTransactionsForTrainer = async (trainerId: string) => {
    const productPromise = ProfileAdministrationAPI.fetchProducts(trainerId);
    const allProductsPromise = fetchAllProductsForTrainer(trainerId);
    const transactionsPromise = ProfileAdministrationAPI.getTransactions(trainerId);

    const [trainerProducts, allTrainerProducts, trainerTransactions] = await Promise.all([
        productPromise,
        allProductsPromise,
        transactionsPromise,
    ]);

    return {
        products: trainerProducts,
        allProducts: allTrainerProducts,
        // Transactions arrive already sorted by date (newest first) from the backend.
        transactions: trainerTransactions,
    };
};

/**
 * Builds a list of editable products for a trainer by combining purchased products with all available products,
 * marking which are already purchased and setting quantities accordingly.
 * @param trainerId 
 * @param trainerProducts 
 * @param allTrainerProducts 
 */
const buildEditableProducts = (
    trainerId: string,
    trainerProducts: PurchasedProduct[],
    allTrainerProducts: Product[],
) => {
    const notPurchasedProducts = allTrainerProducts.filter(
        (product) => !trainerProducts.some((tp) => tp.product === product.productNo)
    );

    const editableProducts: EditableProduct[] = trainerProducts.map(p => ({
        ...p,
        isPurchased: true,
    }));

    const availableEditableProducts: EditableProduct[] = notPurchasedProducts.map(product => ({
            id: 0,
            product: product.productNo,
            trainer: trainerId,
            quantity: 0,
            isPurchased: false,
    }));

    const allEditableProducts: EditableProduct[] = [
        ...editableProducts,
        ...availableEditableProducts,
    ];

    return allEditableProducts;
};

/**
 * Ensures that the products and transactions for a trainer are loaded, setting loading states accordingly to prevent duplicate requests.
 * @param trainerId 
 */
const ensureTrainerDataLoaded = async (trainerId: string) => {
    if (trainerDataLoading.value[trainerId] || editableProducts.value[trainerId]) {
        return;
    }

    trainerDataLoading.value[trainerId] = true;
    try {
        const trainerData = await loadTransactionsForTrainer(trainerId);
        editableProducts.value[trainerId] = buildEditableProducts(
            trainerId,
            trainerData.products,
            trainerData.allProducts,
        );
        transactions.value[trainerId] = trainerData.transactions;
    } catch (error) {
        showError(error, t("messages.load.error"));
    } finally {
        trainerDataLoading.value[trainerId] = false;
    }
};

const onRowExpand = async (event: { data: Trainer }) => {
    await ensureTrainerDataLoaded(event.data.id);
};

/**
 * Handles the selection of a new distributor for a trainer, showing a confirmation dialog before applying changes.
 * @param trainer 
 * @param distributor 
 */
const selectDistributor = async (trainer: Trainer, distributor: Distributor) => {
    const oldDistributor = trainer.distributor;
    if (!distributor || oldDistributor?.id === distributor?.id) {
        return;
    }

    // The backend resets monthly billing (purchase limit -> 0) on a distributor change, so
    // warn about that consequence when the trainer currently has it enabled.
    const message = t('trainer.distributor.confirmation.message', { trainer: trainer.firstname + ' ' + trainer.lastname, oldDistributor: oldDistributor?.company, distributor: distributor.company })
        + (trainer.purchaseLimit > 0 ? `</br><strong>${t('trainer.distributor.confirmation.monthlyBillingHint')}</strong>` : "");

    confirm.require({
        group: "distributor-change",
        message,
        header: t('trainer.distributor.confirmation.header'),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: t("buttons.save"),
        },
        reject: () => {
            selectedDistributor = oldDistributor;
            selectedForDropdown.value = null;
        },
        accept: async () => {
            trainer.distributor = distributor;
            try {
                // The backend disables monthly billing on a distributor change, so sync the
                // purchase limit from the response to keep the billing column truthful.
                const updatedTrainer = await ProfileAdministrationAPI.putTrainer(trainer);
                trainer.purchaseLimit = updatedTrainer.purchaseLimit;
            } catch (error) {
                // Put the old distributor back: the row must not claim a change the backend refused.
                trainer.distributor = oldDistributor;
                showError(error, t("messages.save.error"));
            }
            selectedForDropdown.value = null;
        },
    });
};

const toggleDistributorDropdown = (trainer: Trainer) => {
    if (selectedForDropdown.value === trainer) {
        selectedForDropdown.value = null;
    } else {
        selectedDistributor = trainer.distributor;
        selectedForDropdown.value = trainer;
    }
};

/**
 * Toggles the monthly billing status for a trainer after an explicit confirmation, updating their
 * purchase limit and saving the change via the API, then refreshing the trainers list.
 * @param trainer
 */
const toggleMonthlyBilling = (trainer: Trainer) => {
    const enabling = trainer.purchaseLimit <= 0;
    const newLimit = enabling ? 500 : 0;
    confirm.require({
        group: "monthly-billing-toggle",
        message: t(
            enabling ? "trainer.billingConfirmation.enable" : "trainer.billingConfirmation.disable",
            { trainer: trainer.firstname + " " + trainer.lastname, limit: newLimit },
        ),
        header: t("trainer.billingConfirmation.header"),
        icon: "pi pi-exclamation-triangle",
        rejectProps: {
            label: t("buttons.cancel"),
            severity: "secondary",
            outlined: true,
        },
        acceptProps: {
            label: t("buttons.save"),
        },
        accept: async () => {
            const previousLimit = trainer.purchaseLimit;
            trainer.purchaseLimit = newLimit;
            try {
                await ProfileAdministrationAPI.putTrainer(trainer);
            } catch (error) {
                trainer.purchaseLimit = previousLimit;
                showError(error, t("messages.save.error"));
                return;
            }
            await fetchTrainers();
        },
    });
};

function openEditQuantityDialog(productValue: PurchasedProduct) {
    product.value = productValue;
    editQuantityDialogVisible.value = true;
}

/**
 * Saves the new quantity for a product by calling the API, then refreshes the trainer's products and transactions to reflect changes.
 * 'delete' invalidates the cache for that trainer's products and transactions, forcing a reload when the dialog is closed.
 */
async function saveNewQuantity() {
    if (!product.value) {
        return;
    }
    try {
        await ProfileAdministrationAPI.manageProductQuantityFromAdmin(product.value, quantity.value);
    } catch (error) {
        // The dialog stays open with the entered amount, so the change is not silently lost.
        showError(error, t("messages.save.error"));
        return;
    }
    delete editableProducts.value[product.value.trainer];
    delete transactions.value[product.value.trainer];
    await ensureTrainerDataLoaded(product.value.trainer);
    editQuantityDialogVisible.value = false;
}

// Reset quantity when dialog is closed
watch(editQuantityDialogVisible, (newVal) => {
    if (!newVal) quantity.value = 0;
});

onMounted(async () => {
    const emailQuery = route.query.email;
    if (typeof emailQuery === "string") {
        email.value = emailQuery;
    } else if (Array.isArray(emailQuery)) {
        const firstEmailValue = emailQuery.find((value): value is string => typeof value === "string");
        if (firstEmailValue) {
            email.value = firstEmailValue;
        }
    }
    distributors.value = await ProfileAdministrationAPI.fetchDistributors();
    await fetchTrainers();
});
</script>

<template>
    <ConfirmDialog group="distributor-change">
        <template #message="slotProps">
            <p v-html="slotProps.message.message"></p>
        </template>
    </ConfirmDialog>
    <ConfirmDialog group="monthly-billing-toggle">
        <template #message="slotProps">
            <p v-html="slotProps.message.message"></p>
        </template>
    </ConfirmDialog>
    <h1 class="page-title">{{ t("trainer.title") }}</h1>
    <div class="mt-8 mb-8">
        <SupportSearchBar
            v-model="email"
            input-id="trainers-table_input_trainer"
            button-id="trainers-table_button_search"
            :placeholder="t('trainer.search')"
            :hint="t('projects.input.instruction')"
            @search="searchTrainers"
        />
    </div>
    <!-- Main table -->
    <Message v-if="searched && !trainers.length" severity="info" :closable="false">
        {{ t("trainer.noResults") }}
    </Message>
    <DataTable
        id="trainers-table_table_trainers"
        :value="trainers"
        v-model:expanded-rows="expandedRows"
        v-if="trainers.length > 0"
        paginator
        :rows="20"
        :always-show-paginator="false"
        data-key="id"
        @rowExpand="onRowExpand"
    >
        <Column class="expander" expander style="width: 3rem" />
        <Column field="name" :header="t('trainer.name')">
            <template #body="slotProps">
                {{ slotProps.data.firstname.concat(" " + slotProps.data.lastname) }}
            </template>
        </Column>
        <Column field="email" :header="t('trainer.email')" />
        <Column field="distributor.company" :header="t('trainer.distributor.label')">
            <template #body="slotProps">
                <div class="flex items-center gap-2">
                    <template v-if="selectedForDropdown === slotProps.data && !slotProps.data.isDistributorAccount">
                        <Select
                            v-model="selectedDistributor"
                            :options="distributors"
                            option-label="company"
                            @change="selectDistributor(slotProps.data, selectedDistributor)"
                            :placeholder="t('trainer.distributor.placeholder')"
                            class="w-full md:w-56"
                        />
                    </template>
                    <template v-else>
                        <span>
                            {{ slotProps.data.distributor.company }}
                        </span>
                        <Button
                            v-if="!slotProps.data.isDistributorAccount"
                            icon="pi pi-pencil"
                            severity="secondary"
                            rounded
                            text
                            @click="toggleDistributorDropdown(slotProps.data)"
                        />
                        <i
                            v-else
                            class="pi pi-check ml-2 p-1"
                            v-tooltip="t('trainer.distributor.name')"
                        ></i>
                    </template>
                </div>
            </template>
        </Column>
        <Column field="purchaseLimit" :header="t('trainer.billing')">
            <template #body="slotProps">
                <Button
                    v-if="slotProps.data.purchaseLimit > 0"
                    icon="pi pi-check-circle"
                    severity="success"
                    @click="toggleMonthlyBilling(slotProps.data)"
                />
                <Button
                    v-if="slotProps.data.purchaseLimit <= 0"
                    icon="pi pi-times-circle"
                    severity="danger"
                    @click="toggleMonthlyBilling(slotProps.data)"
                />
            </template>
        </Column>
        <template #expansion="slotProps">
            <div id="trainers-table_table_expansion">
                <h4>{{ slotProps.data.firstname }} {{ slotProps.data.lastname }}</h4>
                <div>
                    <strong>{{ `${t("trainer.id")}:` }}</strong> {{ slotProps.data.id }}
                </div>
                <div>
                    <strong>{{ `${t("trainer.email")}:` }}</strong> {{ slotProps.data.email }}
                </div>
                <div>
                    <strong>{{ `${t("trainer.language")}:` }}</strong> {{ slotProps.data.language }}
                </div>
                <div>
                    <strong>{{ `${t("trainer.recipient")}:` }}</strong> {{ slotProps.data.recipient }}
                </div>
                <div v-if="slotProps.data.address">
                    <strong>{{ `${t("trainer.address")}:` }}</strong>
                </div>
                <div v-if="slotProps.data.address">
                    {{ slotProps.data.address?.street }}<br />
                    {{ slotProps.data.address?.postalCode }} {{ slotProps.data.address?.city }}<br />
                    {{ slotProps.data.address?.country }}<br />
                </div>
                <div>
                    <strong>{{ `${t("trainer.distributor.name")}: ` }}</strong
                    ><i :class="slotProps.data.isDistributorAccount ? 'pi pi-check' : 'pi pi-times'" />
                </div>
                <div>
                    <strong>{{ `${t("trainer.distributor.isStockKeepingDistributor")}: ` }}</strong
                    ><i :class="slotProps.data.isStockKeepingDistributor ? 'pi pi-check' : 'pi pi-times'" />
                </div>
                <EmailDeliveryStatus
                    v-if="slotProps.data.email && slotProps.data.id"
                    :email="slotProps.data.email"
                    :reference-type="EmailReferenceType.TRAINER"
                    :reference-id="String(slotProps.data.id)"
                    class="mt-4"
                />
            </div>
            <div>
                <h3>{{ t("trainer.products") }}</h3>
                <ProgressSpinner v-if="trainerDataLoading[slotProps.data.id]" style="width: 2rem; height: 2rem" strokeWidth="8" />
                <DataTable
                    v-else
                    sortField="product"
                    :sortOrder="1"
                    :id="'trainers-table_table_product-' + slotProps.data.id"
                    :value="editableProducts[slotProps.data.id] || []"
                    paginator
                    :rows="10"
                    :always-show-paginator="false"
                >
                    <Column field="product" :header="t('trainer.product')" sortable>
                        <template #body="props">
                            <Tag severity="info" style="font-weight: normal" :value="props.data.product.toUpperCase()" />
                        </template>
                    </Column>
                    <Column field="isPurchased" :header="t('trainer.isOwned')">
                        <template #body="props">
                            <i
                                :data-cy="`is-purchased-${props.data.product}`"
                                :class="props.data.isPurchased ? 'pi pi-check' : 'pi pi-times'"
                            />
                        </template>
                    </Column>
                    <Column field="quantity" :header="t('trainer.quantity.label')" sortable>
                        <template #body="props">
                            <span :data-cy="`quantity-${props.data.product}`">{{ props.data.quantity }}</span>
                        </template>
                    </Column>
                    <Column field="actions" :header="t('trainer.actions')">
                        <template #body="props">
                            <div class="flex gap-2">
                                <Button :data-cy="`edit-quantity-button-${props.data.product}`" outlined rounded severity="secondary" icon="pi pi-pencil" @click="openEditQuantityDialog(props.data)" />
                            </div>
                        </template>
                    </Column>
                </DataTable>
            </div>
            <div>
                <h3>{{ t("trainer.transactions") }}</h3>
                <DataTable
                    :id="`trainers-table_table_transaction-${slotProps.data.id}`"
                    :value="transactions[slotProps.data.id] || []"
                    paginator
                    :rows="10"
                    :rows-per-page-options="[10, 20, 50]"
                    :always-show-paginator="(transactions[slotProps.data.id]?.length ?? 0) > 10"
                >
                    <Column field="date" :header="t('trainer.date')">
                        <template #body="props">
                            {{ formatDateTime(props.data.date) }}
                        </template>
                    </Column>
                    <Column field="product" :header="t('trainer.product')">
                        <template #body="props">
                            <Tag severity="info" style="font-weight: normal" :value="props.data.product.productNo.toUpperCase()" />
                        </template>
                    </Column>
                    <Column field="type" :header="t('trainer.transaction.type.label')">
                        <template #body="props">
                            {{ t(`header.accountSettings.transactionTable.types.${props.data.type.toLowerCase()}`) }}
                        </template>
                    </Column>
                    <Column field="quantity" :header="t('trainer.quantity.label')"></Column>
                    <Column field="oldBalance" :header="t('trainer.old.balance.label')"></Column>
                    <Column field="newBalance" :header="t('trainer.new.balance.label')"></Column>
                </DataTable>
            </div>
        </template>
    </DataTable>
    <Dialog
        v-model:visible="editQuantityDialogVisible"
        modal
        :header="`${t('trainer.quantity.action')} (${product?.product?.toUpperCase() ?? ''})`"
        :style="{ width: '40rem' }"
        :breakpoints="{ '1199px': '75vw', '575px': '90vw' }"
    >
        <div style="display: flex; flex-direction: column; align-items: center; justify-content: center;">
            <span class="m-2">{{ t('trainer.quantity.instruction') }}</span>
            <InputNumber
                :data-cy="`input-number-${product?.product}`"
                v-model="quantity"
                show-buttons
                button-layout="horizontal"
                decrement-button-class="p-button-danger"
                increment-button-class="p-button-success"
                :min="-((product?.quantity ?? 0))"
            >
                <template #incrementicon>
                    <span class="pi pi-plus" />
                </template>
                <template #decrementicon>
                    <span class="pi pi-minus" />
                </template>
            </InputNumber>
            <div style="margin-top: 0.5rem;">
                <span><b>{{ t('trainer.quantity.newTotal', { total: Math.max(0, (product?.quantity ?? 0) + quantity) }) }}</b></span>
            </div>
        </div>
        <template #footer>
            <div style="width: 100%; display: flex; justify-content: center; align-items: center; gap: 1rem; margin: 0;">
                <Button :label="t('buttons.cancel')" icon="pi pi-times" severity="secondary" @click="editQuantityDialogVisible = false" />
                <Button :data-cy="`save-quantity-button-${product?.product}`" :label="t('buttons.save')" icon="pi pi-check" @click="saveNewQuantity" autofocus />
            </div>
        </template>
    </Dialog>
</template>

